import { useEffect, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { CustomFormFieldComponent } from 'src/components/FormField';
import { IEditProfileForm } from 'src/components/User/EditProfileModal';
import { CustomErrorMessage } from './StepThree';
import CustomFormFieldTextInput from 'src/components/FormField/CustomFormFieldTextInput';
import { Box, Flex, Text, Tooltip } from '@chakra-ui/react';
import { FieldLabel } from 'src/components/Field';
import {
  Button,
  ButtonIcon,
  ButtonText,
  Checkbox,
  FormFeedback,
  Input,
  Modal
} from '@morressier/alembic';
import ORCIDValidator from '../ORCIDValidator';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEnvelope,
  faInfoCircle,
  faPlus,
  faTrash
} from '@fortawesome/pro-regular-svg-icons';
import { faEdit } from '@fortawesome/pro-light-svg-icons';
import PhoneNumberInput from 'src/components/FormField/common/PhoneNumberInput';
import { validateEmail } from 'src/pages/Sign-Up/SignUpForm';

const ProfileDetails = ({
  configs,
  customErrorMessage,
  setCustomErrorMessage,
  primaryEmail,
  isEditablePrimaryEmail,
  showAdditionalEmails
}: {
  configs: any;
  setCustomErrorMessage: (value: CustomErrorMessage) => void;
  customErrorMessage: CustomErrorMessage;
  primaryEmail: string;
  isEditablePrimaryEmail?: boolean;
  showAdditionalEmails?: boolean;
}) => {
  const {
    watch,
    setValue,
    register,
    getValues,
    control,
    formState: { errors }
  } = useFormContext<IEditProfileForm>();
  const [orcidError, setOrcidError] = useState('');
  const [isEditPrimaryEmailOpen, setIsEditPrimaryEmailOpen] = useState(false);
  const [currentPrimaryEmail, setCurrentPrimaryEmail] = useState(primaryEmail);
  const isSameAsSecondaryEmail = watch('isSameAsSecondaryEmail', false);
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'additionalEmails'
  });
  const additionalEmails = watch('additionalEmails') ?? [];
  const primaryEmailValue = currentPrimaryEmail.trim().toLowerCase();
  const secondaryEmailValue = (watch('secondaryEmail') ?? '')
    .trim()
    .toLowerCase();
  const ccEmailValue = (watch('ccEmail') ?? '').trim().toLowerCase();

  const onCheckboxChange = (checked: boolean) => {
    setValue('isSameAsSecondaryEmail', checked);
    if (checked) {
      const secondaryEmail = getValues('secondaryEmail');
      setValue('ccEmail', secondaryEmail);
    } else {
      setValue('ccEmail', ''); // Reset to empty on uncheck
    }
  };

  useEffect(() => {
    setCustomErrorMessage({
      ...customErrorMessage,
      orcid: orcidError ? true : false
    });
  }, [orcidError]);

  useEffect(() => {
    setCurrentPrimaryEmail(primaryEmail);
  }, [primaryEmail]);

  const handleSavePrimaryEmail = (email: string) => {
    setValue('email', email, { shouldValidate: true, shouldDirty: true });
    setCurrentPrimaryEmail(email);
  };

  const validateAdditionalEmail = (value: string | undefined) => {
    const normalizedValue = value?.trim().toLowerCase() ?? '';
    const normalizedEmails = additionalEmails
      .map(item => item?.email?.trim().toLowerCase())
      .filter((email): email is string => Boolean(email));

    if (!normalizedValue) {
      return true;
    }

    if (!validateEmail.test(normalizedValue)) {
      return 'Please enter a valid email address.';
    }

    if (normalizedValue === primaryEmailValue) {
      return 'Additional emails must be different from the primary email address.';
    }

    if (secondaryEmailValue && normalizedValue === secondaryEmailValue) {
      return 'Additional emails must be different from the secondary email address.';
    }

    if (ccEmailValue && normalizedValue === ccEmailValue) {
      return 'Additional emails must be different from the CC email address.';
    }

    const duplicateCount = normalizedEmails.filter(
      email => email === normalizedValue
    ).length;

    if (duplicateCount > 1) {
      return 'Additional emails must be unique.';
    }

    return true;
  };

  return (
    <>
      <Box
        display="grid"
        gridTemplateAreas={{
          base: `"prefix" "first" "middle" "last"`,
          md: `"prefix first" "middle last"`,
          xl: `"prefix first middle last"`
        }}
        gridTemplateColumns={{
          base: '1fr',
          md: '1fr 1fr',
          xl: '120px 1fr 1fr 1fr'
        }}
        gap={4}
        alignItems="start"
      >
        <Box gridArea="prefix">
          {configs?.Prefix && (
            <CustomFormFieldComponent config={configs?.Prefix} />
          )}
        </Box>
        {configs?.FirstName && (
          <Box gridArea="first">
            <CustomFormFieldTextInput config={configs?.FirstName} />
          </Box>
        )}
        {configs?.MiddleName && (
          <Box gridArea="middle">
            <CustomFormFieldTextInput config={configs?.MiddleName} />
          </Box>
        )}
        {configs?.LastName && (
          <Box gridArea="last">
            <CustomFormFieldTextInput config={configs?.LastName} />
          </Box>
        )}
      </Box>

      {configs?.ORCID && (
        <ORCIDValidator
          config={configs?.ORCID}
          orcidError={orcidError}
          setOrcidError={setOrcidError}
        />
      )}

      {currentPrimaryEmail && (
        <Flex direction={'column'} gap={3} flex={1}>
          <Flex alignItems={'center'} gap={1}>
            <FieldLabel label="Primary Email" subText="" isRequired />
            <Tooltip
              hasArrow
              shouldWrapChildren
              placement="top"
              label={
                'Primary address for all submission and decision correspondence'
              }
              borderRadius={4}
              data-cy="info-tooltip"
            >
              <ButtonIcon
                size="small"
                ariaLabel={currentPrimaryEmail}
                icon={<FontAwesomeIcon icon={faInfoCircle} />}
                data-cy="info-icon"
              />
            </Tooltip>{' '}
            <ButtonText
              type="button"
              justifyItems={'center'}
              alignItems={'center'}
              fontSize={'small'}
              onClick={() => setIsEditPrimaryEmailOpen(true)}
              data-cy="edit-primary-email"
            >
              <FontAwesomeIcon icon={faEdit} />
              <Text mt={'0.5'}>Edit</Text>
            </ButtonText>
          </Flex>
          <Flex gap={2} alignItems={'center'}>
            <Input
              name={'primaryEmail'}
              value={currentPrimaryEmail}
              isReadOnly
              aria-label={'primaryEmail'}
              cursor="pointer"
            />
          </Flex>
        </Flex>
      )}

      {isEditablePrimaryEmail && !primaryEmail && (
        <Flex direction={'column'} flex={1} gap={1}>
          <FieldLabel label="Primary Email" subText="" isRequired />
          <Input
            placeholder="Please provide a primary email address"
            {...register('email', {
              required: 'Primary email address is required',
              maxLength: {
                value: 128,
                message: 'Primary email address must not exceed 128 characters'
              },
              pattern: {
                value: validateEmail,
                message: 'Please enter a valid primary email address'
              }
            })}
            isInvalid={!!errors.email}
            aria-label={'primaryEmail'}
          />
          {errors.email && (
            <FormFeedback variant="error">{errors.email?.message}</FormFeedback>
          )}
        </Flex>
      )}

      {configs?.SecondaryEmail && (
        <CustomFormFieldTextInput
          config={configs?.SecondaryEmail}
          isShowHelpInfo={true}
        />
      )}

      <Flex direction="column" gap={3} flex={1}>
        {configs?.ccEmail && (
          <CustomFormFieldTextInput
            config={configs?.ccEmail}
            isShowHelpInfo={true}
            isEditingAllowed={!isSameAsSecondaryEmail}
            additionalElement={
              <Flex alignItems={'center'}>
                <Checkbox
                  id="sameAsSecondaryEmail"
                  {...register('isSameAsSecondaryEmail')}
                  ariaLabel="Same as secondary email"
                  isChecked={isSameAsSecondaryEmail}
                  onChange={e => onCheckboxChange(e.target.checked)}
                  // disabled={!getValues('secondaryEmail')}
                >
                  Same as secondary email
                </Checkbox>
                <Tooltip
                  hasArrow
                  shouldWrapChildren
                  placement="top-start"
                  label="Check to auto-copy your Secondary Email into the CC Email field"
                  borderRadius={4}
                  data-cy="info-tooltip"
                >
                  <ButtonIcon
                    size="small"
                    ariaLabel={'ccEmailCheck'}
                    icon={<FontAwesomeIcon icon={faInfoCircle} />}
                    data-cy="info-icon"
                  />
                </Tooltip>
              </Flex>
            }
          />
        )}
      </Flex>

      {showAdditionalEmails && (
        <Flex direction="column" gap={2} flex={1}>
          <Flex gap={2} alignItems="center">
            <FieldLabel
              label="Additional Emails"
              subText=""
              isRequired={false}
            />
            {fields.length === 0 && (
              <ButtonText
                type="button"
                leftIcon={<FontAwesomeIcon icon={faPlus} />}
                onClick={() => append({ email: '' })}
                data-cy="add-additional-email"
              >
                Add
              </ButtonText>
            )}
          </Flex>

          <Flex direction="column" gap={3} w="full">
            {fields.map((field, index) => {
              const fieldName = `additionalEmails.${index}.email` as const;

              return (
                <Box
                  key={field.id}
                  display="grid"
                  gridTemplateColumns="1fr 82px"
                  gap={2}
                  alignItems="start"
                  w="full"
                >
                  <Box w="full">
                    <Input
                      placeholder="Enter additional email"
                      {...register(fieldName, {
                        validate: validateAdditionalEmail
                      })}
                      isInvalid={Boolean(
                        errors.additionalEmails?.[index]?.email
                      )}
                      aria-label={`additional-email-${index}`}
                    />
                    {errors.additionalEmails?.[index]?.email && (
                      <FormFeedback variant="error">
                        {errors.additionalEmails[index]?.email?.message}
                      </FormFeedback>
                    )}
                  </Box>

                  <Flex gap={2} justifyContent="flex-start" w="82px">
                    {index === fields.length - 1 && (
                      <ButtonIcon
                        type="button"
                        ariaLabel="Add additional email"
                        icon={<FontAwesomeIcon icon={faPlus} />}
                        onClick={() => append({ email: '' })}
                        data-cy="add-additional-email"
                      />
                    )}
                    <ButtonIcon
                      type="button"
                      ariaLabel="Remove additional email"
                      icon={<FontAwesomeIcon icon={faTrash} />}
                      onClick={() => remove(index)}
                      data-cy="remove-additional-email"
                    />
                  </Flex>
                </Box>
              );
            })}
          </Flex>
        </Flex>
      )}

      {configs?.Degree && <CustomFormFieldTextInput config={configs?.Degree} />}
      {configs?.JobTitle && (
        <CustomFormFieldTextInput config={configs?.JobTitle} />
      )}

      {configs?.PhoneNumber && (
        <PhoneNumberInput config={configs?.PhoneNumber} />
      )}
      {configs?.LinkedinProfileUrl && (
        <CustomFormFieldTextInput config={configs?.LinkedinProfileUrl} />
      )}

      <UpdatePrimaryEmailModal
        isOpen={isEditPrimaryEmailOpen}
        onClose={() => setIsEditPrimaryEmailOpen(false)}
        currentEmail={currentPrimaryEmail}
        onSave={handleSavePrimaryEmail}
      />
    </>
  );
};

const UpdatePrimaryEmailModal = ({
  isOpen,
  onClose,
  currentEmail,
  onSave
}: {
  isOpen: boolean;
  onClose: () => void;
  currentEmail: string;
  onSave: (email: string) => void;
}) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');

  const handleClose = () => {
    setEmail('');
    setError('');
    onClose();
  };

  const handleSave = () => {
    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      setError('Primary email address is required');
      return;
    }

    if (trimmedEmail.length > 128) {
      setError('Primary email address must not exceed 128 characters');
      return;
    }

    if (!validateEmail.test(trimmedEmail)) {
      setError('Please enter a valid primary email address');
      return;
    }

    if (trimmedEmail.toLowerCase() === currentEmail?.trim().toLowerCase()) {
      setError(
        'New email address must be different from your current email address.'
      );
      return;
    }

    onSave(trimmedEmail);
    handleClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {}}
      closeButtonProps={{ visibility: 'hidden' }}
      width="700px"
    >
      <Flex direction={'column'} px={8} pt={8}>
        <Flex direction={'column'} gap={3}>
          <Box>
            <FontAwesomeIcon icon={faEnvelope} size="2xl" />
          </Box>
          <Text textStyle="headingGroup">Update Primary Email</Text>
          <Text textStyle="textBody">Current email: {currentEmail}</Text>

          <Flex direction={'column'} gap={5}>
            <Flex direction={'column'} gap={3} flex={1}>
              <FieldLabel label="New Primary Email" subText="" isRequired />
              <Input
                name={'newPrimaryEmail'}
                placeholder="Enter your new primary email address"
                value={email}
                onChange={event => {
                  setEmail(event.target.value);
                  if (error) setError('');
                }}
                isInvalid={!!error}
                aria-label={'primaryEmail'}
              />
              {error && <FormFeedback variant="error">{error}</FormFeedback>}
            </Flex>

            <Flex justifyContent={'flex-end'} gap={3} mb={5}>
              <Button variant="secondary" onClick={handleClose}>
                Exit Without Saving
              </Button>
              <Button onClick={handleSave}>Save Changes</Button>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </Modal>
  );
};

export default ProfileDetails;
